import { Avatar } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import {
  resolveQuestionAsker,
  resolveQuestionDisplayTime,
  resolveQuestionMessageBody,
  resolveQuestionPrompt,
} from '../../../utils/buildQuestionThreadSeed.js';

/** Prototype aims-htl: dark circle, light initials. */
const AVATAR_CLASS =
  'border-transparent bg-neutral-800 text-[11px] font-semibold text-white dark:bg-neutral-700';

/**
 * Prototype MESSAGE / QUESTION card — label, author meta, body.
 */
function QuestionMessageBlock({ event, detail, situation, askedAt }) {
  const { t, i18n } = useTranslation('work-queues-app');
  const isBroadcast = detail?.questionVariant === 'broadcast';
  const sectionLabel = isBroadcast ? t('question.messageSection') : t('question.questionSection');
  const asker = resolveQuestionAsker(event) ?? detail?.askedBy;
  const displayAt = askedAt ?? resolveQuestionDisplayTime(event, i18n.language);
  const messageBody = isBroadcast ? resolveQuestionMessageBody(event) ?? situation ?? detail?.context : null;
  const prompt = resolveQuestionPrompt(event);
  const showBroadcastAsk =
    isBroadcast && Boolean(prompt) && (!messageBody || prompt.trim() !== String(messageBody).trim());
  const bodyText = isBroadcast ? messageBody : prompt;

  const meta = [asker?.role, displayAt].filter(Boolean).join(' · ');

  return (
    <div className="rounded-lg border border-edge bg-surface-elevated px-4 py-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-content-faint">{sectionLabel}</p>

      {asker ? (
        <div className="mt-3 flex items-start gap-3">
          <Avatar
            size="md"
            initials={asker.initials}
            alt={asker.name}
            className={AVATAR_CLASS}
          />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold leading-snug text-content">{asker.name}</p>
            {meta ? <p className="mt-0.5 text-xs text-content-faint">{meta}</p> : null}
          </div>
        </div>
      ) : null}

      {bodyText ? (
        <p className="mt-3 text-sm leading-relaxed text-content-secondary">{bodyText}</p>
      ) : null}

      {showBroadcastAsk ? (
        <p className="mt-4 text-base font-semibold leading-snug text-content">{prompt}</p>
      ) : null}

      {detail?.context && isBroadcast && messageBody && detail.context !== messageBody && !showBroadcastAsk ? (
        <div className="mt-4 border-t border-edge pt-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-content-faint">
            {t('question.context')}
          </p>
          <p className="mt-1 text-sm text-content-secondary">{detail.context}</p>
        </div>
      ) : null}
    </div>
  );
}

export default QuestionMessageBlock;
